import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription, interval } from "rxjs";
import { filter, take } from "rxjs/operators";

import { Logger } from "./logger.service";
import {ToastService} from './toast.service';

interface VersionManifest {
  buildHash?: string;
  version?: string;
  imageTag?: string;
}

const log = new Logger("VersionCheckService");

@Injectable({ providedIn: "root" })
export class VersionCheckService implements OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly versionCheckIntervalMs = 5 * 60 * 1000;
  private currentBuildHash: string | null = null;
  private isChecking = false;
  private isStarted = false;
  private hasUpdateToast = false;

  constructor(
    private router: Router,
    private toastr: ToastService
  ) {}

  startMonitoring(): void {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;
    this.subscriptions.add(interval(this.versionCheckIntervalMs).subscribe(() => void this.checkNow()));
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => void this.checkNow())
    );

    void this.checkNow();
  }

  async checkNow(): Promise<void> {
    if (this.isChecking || typeof window === "undefined") {
      return;
    }

    this.isChecking = true;

    try {
      const manifest = await this.fetchVersionManifest();
      const nextBuildHash = manifest?.buildHash;

      if (!nextBuildHash) {
        return;
      }

      if (!this.currentBuildHash) {
        this.currentBuildHash = nextBuildHash;
        return;
      }

      if (nextBuildHash !== this.currentBuildHash) {
        this.showUpdateToast(manifest);
      }
    } catch (error) {
      log.debug("Version check failed", error);
    } finally {
      this.isChecking = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private async fetchVersionManifest(): Promise<VersionManifest | null> {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      return null;
    }

    return response.json() as Promise<VersionManifest>;
  }

  private showUpdateToast(manifest: VersionManifest): void {
    if (this.hasUpdateToast) {
      return;
    }

    this.hasUpdateToast = true;

    const versionLabel = manifest.imageTag || manifest.version || "latest build";
    const toast = this.toastr.info(`New version available (${versionLabel}). Click to reload.`, undefined, {
      disableTimeOut: true,
      tapToDismiss: false,
      closeButton: true,
      positionClass: "toast-bottom-right",
    });

    this.subscriptions.add(
      toast.onTap.pipe(take(1)).subscribe(() => {
        this.reloadPage();
      })
    );

    this.subscriptions.add(
      toast.onHidden.pipe(take(1)).subscribe(() => {
        this.hasUpdateToast = false;
      })
    );
  }

  private reloadPage(): void {
    window.location.reload();
  }
}
